import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useLocale } from '../contexts/LocaleContext'
import { getCityUrl, getAffiliateLink, getBaseSlugFromUrl } from '../config/site'
import { updateSEO } from '../utils/seo'
import { loadArticle } from '../utils/loadArticle'
import { markdownToHtml } from '../utils/markdownToHtml'
import citiesData from '../data/cities.json'
import './CityPage.css'

const CityPage = () => {
  const { citySlug } = useParams()
  const navigate = useNavigate()
  const { t, locale, translations } = useLocale()
  const [city, setCity] = useState(null)
  const [article, setArticle] = useState(null)
  const [loading, setLoading] = useState(true)

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  useEffect(() => {
    // Extract base slug from URL (remove "trans-treffen-" prefix)
    const baseSlug = getBaseSlugFromUrl(citySlug)
    
    // Find city by base slug
    const foundCity = citiesData.find(
      (c) => c.Slug.toLowerCase() === baseSlug?.toLowerCase()
    )
    
    if (foundCity) {
      setCity(foundCity)
      
      // Load article data
      const fetchArticle = async () => {
        setLoading(true)
        const articleData = await loadArticle(foundCity.Slug, locale, 'city')
        setArticle(articleData)
        
        // Update SEO meta tags
        if (articleData?.metaTitle) {
          updateSEO({
            title: articleData.metaTitle,
            description: articleData.metaDescription,
            keywords: articleData.metaKeywords,
            url: getCityUrl(foundCity.Slug)
          })
        } else {
          // Use default SEO
          updateSEO({
            title: t('seo.cityTitle', { city: foundCity.City }),
            description: t('seo.cityDescription', { 
              city: foundCity.City, 
              population: foundCity.Population.toLocaleString('de-DE') 
            }),
            keywords: t('seo.cityKeywords', { city: foundCity.City }),
            url: getCityUrl(foundCity.Slug)
          })
        }
        setLoading(false)
      }
      
      fetchArticle()
    } else {
      // City not found, redirect to home
      navigate('/', { replace: true })
    }
  }, [citySlug, navigate, t, locale])

  if (!city || loading) {
    return (
      <div className="city-page-container">
        <div className="loading">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="city-page-container">
      <button className="back-button" onClick={() => navigate('/')}>
        ← {t('city.backToHome')}
      </button>

      <header className="city-header">
        <div className="city-hero-image-wrapper">
          <img 
            src="/images/city-hero.png" 
            alt={`Trans Treffen in ${city.City}`}
            className="city-hero-image"
            loading="eager"
            onError={(e) => {
              // Fallback: hide image if it doesn't exist yet
              e.target.style.display = 'none';
            }}
          />
          <div className="city-hero-overlay"></div>
        </div>
        <div className="city-header-content">
          <div className="city-badge">
            <span className="city-badge-icon">📍</span>
            <span>{city.City}</span>
          </div>
          <h1 className="city-page-title">
            {article?.title || t('city.welcome', { city: city.City })}
          </h1>
          <div className="city-stats">
            <div className="city-stat">
              <span className="city-stat-icon">👥</span>
              <span className="city-stat-value">{city.Population.toLocaleString('de-DE')}</span>
              <span className="city-stat-label">{t('common.population')}</span>
            </div>
            <div className="city-stat">
              <span className="city-stat-icon">💕</span>
              <span className="city-stat-value">Aktiv</span>
              <span className="city-stat-label">Community</span>
            </div>
          </div>
        </div>
      </header>

      <div className="city-content">
        {article ? (
          <>
            {/* Table of Contents */}
            {(article.guide || article.places || article.tips || article.faq) && (
              <div className="table-of-contents">
                <h3 className="toc-title">📑 Inhaltsverzeichnis</h3>
                <ul className="toc-list">
                  {article.introduction && (
                    <li><a href="#introduction" onClick={(e) => { e.preventDefault(); scrollToSection('introduction') }}>Einführung</a></li>
                  )}
                  {article.guide && (
                    <li><a href="#guide" onClick={(e) => { e.preventDefault(); scrollToSection('guide') }}>Wie man trans Personen kennenlernt</a></li>
                  )}
                  {article.places && (
                    <li><a href="#places" onClick={(e) => { e.preventDefault(); scrollToSection('places') }}>Beste Orte & Locations</a></li>
                  )}
                  {article.tips && (
                    <li><a href="#tips" onClick={(e) => { e.preventDefault(); scrollToSection('tips') }}>Tipps</a></li>
                  )}
                  {article.faq && (
                    <li><a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq') }}>Häufig gestellte Fragen</a></li>
                  )}
                </ul>
              </div>
            )}

            {/* Introduction Section */}
            {article.introduction && (
              <div id="introduction" className="article-section introduction-section">
                <div className="section-content" dangerouslySetInnerHTML={{ __html: markdownToHtml(article.introduction) }} />
              </div>
            )}

            {/* Legacy content field (for backward compatibility) */}
            {!article.introduction && article.content && (
              <div 
                className="article-section"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(article.content) }}
              />
            )}

            {/* Guide Section */}
            {article.guide && (
              <div id="guide" className="article-section guide-section">
                <div className="section-header">
                  <span className="section-icon">📖</span>
                  <h2>Wie man trans Personen in {city.City} kennenlernt</h2>
                </div>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: markdownToHtml(article.guide) }} />
              </div>
            )}

            {/* Best Places Section */}
            {article.places && (
              <div id="places" className="article-section places-section">
                <div className="section-header">
                  <span className="section-icon">🗺️</span>
                  <h2>Beste Orte & Locations in {city.City}</h2>
                </div>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: markdownToHtml(article.places) }} />
              </div>
            )}

            {/* Tips Section */}
            {article.tips && (
              <div id="tips" className="article-section tips-section">
                <div className="section-header">
                  <span className="section-icon">💡</span>
                  <h2>Tipps für Trans Treffen in {city.City}</h2>
                </div>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: markdownToHtml(article.tips) }} />
              </div>
            )}

            {/* FAQ Section */}
            {article.faq && (
              <div id="faq" className="article-section faq-section">
                <div className="section-header">
                  <span className="section-icon">❓</span>
                  <h2>{article.faq.title || `Häufig gestellte Fragen zu Trans Treffen in ${city.City}`}</h2>
                </div>
                <div className="faq-list">
                  {(article.faq.items || article.faq.questions || []).map((item, i) => (
                    <div key={i} className="faq-item">
                      <h3 className="faq-question">{item.question}</h3>
                      <p className="faq-answer">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Link to global hub */}
            <div className="internal-link-section">
              <p>
                <a href={`/${translations?.pages?.slugs?.globalHub || 'trans-dating'}`} className="internal-link">
                  {t('city.internalLinkToHub')}
                </a>
              </p>
            </div>

            {/* Affiliate Link CTA */}
            <div className="affiliate-cta">
              <a 
                href={getAffiliateLink()} 
                target="_blank" 
                rel="noopener noreferrer"
                className="affiliate-button"
              >
                {t('city.affiliateCTA', { city: city.City })}
              </a>
            </div>
          </>
        ) : (
          <>
            <p className="city-description">
              {t('city.description', { 
                city: city.City, 
                population: city.Population.toLocaleString('de-DE') 
              })}
            </p>
            <div className="internal-link-section">
              <p>
                <a href={`/${translations?.pages?.slugs?.globalHub || 'trans-dating'}`} className="internal-link">
                  {t('city.internalLinkToHub')}
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CityPage
